// ─── Reporting anchor + window model ──────────────────────────────────────────

/** The four supported reporting windows relative to an anchor month. */
export type ReportingWindow =
  | 'DISPLAY_TIMESCALE'
  | 'ROLLING_3'
  | 'ROLLING_4'
  | 'ROLLING_6'
  | 'SINGLE'
  | 'YTD'
  | 'FY'
  | 'ROLLING_12';

/**
 * Dashboard-level reporting context.
 * `reporting_anchor_period` is in YYYY-MM format (e.g. "2026-03").
 * `reporting_window` defines the performance window relative to the anchor.
 */
export interface ReportingContext {
  reporting_anchor_period: string;
  reporting_window: ReportingWindow;
}

/** Human-readable labels for each window type, used in dropdowns. */
export const REPORTING_WINDOW_LABELS: Record<ReportingWindow, string> = {
  SINGLE: 'Single Month',
  YTD: 'Year to Date',
  FY: 'Full Year',
  ROLLING_3: 'Rolling 3 Months',
  ROLLING_4: 'Rolling 4 Months',
  ROLLING_6: 'Rolling 6 Months',
  ROLLING_12: 'Rolling 12 Months',
  DISPLAY_TIMESCALE: 'Display Timescale',
};

// ─── Shared month constant ─────────────────────────────────────────────────────

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

// ─── Foundation helpers (used by both legacy and new code) ────────────────────

/**
 * Formats a Date as MMM-YY (e.g. "Mar-26").
 */
export const formatMonth = (date: Date): string => {
  const month = MONTHS[date.getMonth()];
  const year = date.getFullYear().toString().slice(-2);
  return `${month}-${year}`;
};

/**
 * Returns the 0-based month index for a FY start month abbreviation.
 * Defaults to 0 (January) if the abbreviation is not recognised.
 */
export const getFinancialYearStartMonthIndex = (fyStartMonth: string): number => {
  const index = MONTHS.indexOf(fyStartMonth);
  return index === -1 ? 0 : index;
};

// ─── YYYY-MM ↔ MMM-YY conversion helpers ──────────────────────────────────────

/**
 * Converts a YYYY-MM string (e.g. "2026-03") to a Date set to the 1st of that month.
 * Returns null if the input is invalid.
 */
export const yyyymmToDate = (yyyymm: string): Date | null => {
  const match = yyyymm.match(/^(\d{4})-(\d{2})$/);
  if (!match) return null;
  const year = Number.parseInt(match[1], 10);
  const month = Number.parseInt(match[2], 10) - 1;
  if (month < 0 || month > 11) return null;
  return new Date(year, month, 1);
};

/**
 * Converts a Date to YYYY-MM format (e.g. "2026-03").
 */
export const dateToYyyyMm = (date: Date): string => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  return `${year}-${month}`;
};

/**
 * Converts a YYYY-MM string to MMM-YY format (e.g. "2026-03" → "Mar-26").
 * Returns empty string if input is invalid.
 */
export const yyyymmToMmmYy = (yyyymm: string): string => {
  const date = yyyymmToDate(yyyymm);
  if (!date) return '';
  return formatMonth(date);
};

/**
 * Converts a MMM-YY string (e.g. "Mar-26") to a YYYY-MM string (e.g. "2026-03").
 * Returns empty string if input is invalid.
 */
export const mmmYyToYyyyMm = (mmmYy: string): string => {
  const match = mmmYy.match(/^([A-Za-z]{3})-(\d{2})$/);
  if (!match) return '';
  const monthIndex = MONTHS.indexOf(match[1]);
  if (monthIndex === -1) return '';
  const currentCentury = Math.floor(new Date().getFullYear() / 100) * 100;
  const year = currentCentury + Number.parseInt(match[2], 10);
  return `${year}-${String(monthIndex + 1).padStart(2, '0')}`;
};

// ─── Period resolution ─────────────────────────────────────────────────────────

/**
 * Resolves a reporting anchor + window to a concrete start/end period pair.
 *
 * @param anchorYyyyMm - The anchor month in YYYY-MM format (e.g. "2026-03")
 * @param window - The reporting window type
 * @param fyStartMonth - Three-letter FY start month abbreviation (e.g. "Jan", "Apr")
 * @returns { start, end } both in MMM-YY format (e.g. { start: "Jan-26", end: "Mar-26" })
 */
export const resolveReportingPeriod = (
  anchorYyyyMm: string,
  window: ReportingWindow,
  fyStartMonth: string = 'Jan',
): { start: string; end: string } => {
  const anchor = yyyymmToDate(anchorYyyyMm);
  if (!anchor) return { start: '', end: '' };

  const fyStartIndex = getFinancialYearStartMonthIndex(fyStartMonth);

  switch (window) {
    case 'ROLLING_3': {
      // 3 months ending at anchor
      const start = new Date(anchor.getFullYear(), anchor.getMonth() - 2, 1);
      return { start: formatMonth(start), end: formatMonth(anchor) };
    }
    case 'ROLLING_4': {
      // 4 months ending at anchor
      const start = new Date(anchor.getFullYear(), anchor.getMonth() - 3, 1);
      return { start: formatMonth(start), end: formatMonth(anchor) };
    }
    case 'ROLLING_6': {
      // 6 months ending at anchor
      const start = new Date(anchor.getFullYear(), anchor.getMonth() - 5, 1);
      return { start: formatMonth(start), end: formatMonth(anchor) };
    }
    case 'SINGLE': {
      const period = formatMonth(anchor);
      return { start: period, end: period };
    }
    case 'YTD': {
      // FY start containing anchor → anchor
      const fyStart =
        anchor.getMonth() >= fyStartIndex
          ? new Date(anchor.getFullYear(), fyStartIndex, 1)
          : new Date(anchor.getFullYear() - 1, fyStartIndex, 1);
      return { start: formatMonth(fyStart), end: formatMonth(anchor) };
    }
    case 'FY': {
      // Full financial year containing anchor
      const fyStart =
        anchor.getMonth() >= fyStartIndex
          ? new Date(anchor.getFullYear(), fyStartIndex, 1)
          : new Date(anchor.getFullYear() - 1, fyStartIndex, 1);
      const fyEndIndex = (fyStartIndex - 1 + 12) % 12;
      const fyEndYear =
        fyEndIndex < fyStartIndex ? fyStart.getFullYear() + 1 : fyStart.getFullYear();
      const fyEnd = new Date(fyEndYear, fyEndIndex, 1);
      return { start: formatMonth(fyStart), end: formatMonth(fyEnd) };
    }
    case 'ROLLING_12': {
      // 12 months ending at anchor
      const start = new Date(anchor.getFullYear(), anchor.getMonth() - 11, 1);
      return { start: formatMonth(start), end: formatMonth(anchor) };
    }
    default:
      return { start: '', end: '' };
  }
};

// ─── Macro → ReportingContext mapping ─────────────────────────────────────────

/**
 * Returns the current month in YYYY-MM format.
 */
export const getCurrentMonthYyyyMm = (): string => dateToYyyyMm(new Date());

/**
 * Returns last month in YYYY-MM format.
 */
export const getLastMonthYyyyMm = (): string => {
  const now = new Date();
  return dateToYyyyMm(new Date(now.getFullYear(), now.getMonth() - 1, 1));
};

/**
 * Maps a macro preset name to a ReportingContext (anchor + window).
 * For non-Custom presets the anchor is always computed at call time (never cached),
 * so "Current Month" always means the actual current month.
 *
 * Note: `fyStartMonth` is not needed here because macros only return an anchor+window
 * pair, not resolved dates. Pass it to `resolveReportingPeriod` when you need
 * concrete start/end period strings.
 */
export const macroToReportingContext = (preset: string): ReportingContext => {
  switch (preset) {
    case 'Current Month':
      return { reporting_anchor_period: getCurrentMonthYyyyMm(), reporting_window: 'SINGLE' };
    case 'Last Month':
      return { reporting_anchor_period: getLastMonthYyyyMm(), reporting_window: 'SINGLE' };
    case 'Financial Year to Last Month':
      return { reporting_anchor_period: getLastMonthYyyyMm(), reporting_window: 'YTD' };
    case 'This Financial Year':
      return { reporting_anchor_period: getCurrentMonthYyyyMm(), reporting_window: 'FY' };
    default:
      // Custom or unknown — return a safe default; caller should provide explicit anchor/window
      return { reporting_anchor_period: getCurrentMonthYyyyMm(), reporting_window: 'SINGLE' };
  }
};

// ─── Display formatting ────────────────────────────────────────────────────────

/**
 * Formats an anchor + window as a compact display string for the header/trigger button.
 * e.g. "Mar 2026 • YTD", "Mar 2026 • Full Year", "Mar 2026" (for SINGLE)
 */
export const formatReportingDisplay = (anchorYyyyMm: string, window: ReportingWindow): string => {
  const date = yyyymmToDate(anchorYyyyMm);
  if (!date) return '';
  const monthName = MONTHS[date.getMonth()];
  const year = date.getFullYear();
  const anchorLabel = `${monthName} ${year}`;

  if (window === 'SINGLE') return anchorLabel;
  if (window === 'DISPLAY_TIMESCALE') return 'Display Timescale';

  const windowLabelMap: Partial<Record<ReportingWindow, string>> = {
    YTD: 'YTD',
    FY: 'Full Year',
    ROLLING_12: 'Rolling 12',
    ROLLING_3: 'Rolling 3',
    ROLLING_4: 'Rolling 4',
    ROLLING_6: 'Rolling 6',
  };

  const label = windowLabelMap[window] || window;
  return `${anchorLabel} • ${label}`;
};

/**
 * Formats the badge text shown on an instrument tile when it has a window override.
 * Includes full resolved date ranges where appropriate.
 *
 * @param anchorYyyyMm - Dashboard-level anchor (YYYY-MM)
 * @param window - The instrument's override window
 * @param fyStartMonth - FY start month abbreviation
 */
export const formatInstrumentBadge = (
  anchorYyyyMm: string,
  window: ReportingWindow,
  fyStartMonth: string = 'Jan',
): string => {
  const { start, end } = resolveReportingPeriod(anchorYyyyMm, window, fyStartMonth);
  const anchor = yyyymmToDate(anchorYyyyMm);
  if (!anchor) return '';

  switch (window) {
    case 'SINGLE':
      return end; // e.g. "Mar-26"
    case 'YTD':
      return `YTD (to ${end})`; // e.g. "YTD (to Mar-26)"
    case 'FY': {
      // e.g. "FY26 (Apr–Mar)"
      const startDate = yyyymmToDate(mmmYyToYyyyMm(start));
      const endDate = yyyymmToDate(mmmYyToYyyyMm(end));
      if (!startDate || !endDate) return `FY (${start}–${end})`;
      const fyYear = endDate.getFullYear().toString().slice(-2);
      return `FY${fyYear} (${MONTHS[startDate.getMonth()]}–${MONTHS[endDate.getMonth()]})`;
    }
    case 'ROLLING_3':
      return `Rolling 3 (${start}–${end})`; // e.g. "Rolling 3 (Jan-26–Mar-26)"
    case 'ROLLING_4':
      return `Rolling 4 (${start}–${end})`; // e.g. "Rolling 4 (Dec-25–Mar-26)"
    case 'ROLLING_6':
      return `Rolling 6 (${start}–${end})`; // e.g. "Rolling 6 (Oct-25–Mar-26)"
    case 'ROLLING_12':
      return `Rolling 12 (${start}–${end})`; // e.g. "Rolling 12 (Apr-25–Mar-26)"
    case 'DISPLAY_TIMESCALE':
      return 'Display Timescale';
    default:
      return '';
  }
};

/**
 * Short version of the instrument badge label — only the window identifier,
 * no parenthetical date range. Used when the instrument tile is too narrow
 * to display the full label.
 *
 * e.g. "YTD (to Mar-26)" → "YTD"
 *      "FY26 (Apr–Mar)"  → "FY26"
 *      "Rolling 12 (…)"  → "Rolling 12"
 *      "Mar-26"          → "Mar-26"  (SINGLE has no brackets)
 */
export const formatInstrumentBadgeShort = (
  anchorYyyyMm: string,
  window: ReportingWindow,
  fyStartMonth: string = 'Jan',
): string => {
  const full = formatInstrumentBadge(anchorYyyyMm, window, fyStartMonth);
  // Strip everything from the first ' (' onwards
  const parenIndex = full.indexOf(' (');
  return parenIndex > -1 ? full.slice(0, parenIndex) : full;
};

// ─── Legacy helpers ────────────────────────────────────────────────────────────

export const getCurrentMonth = (): string => formatMonth(new Date());

export const getLastMonth = (): string => {
  const now = new Date();
  const lastMonth = new Date(now.getFullYear(), now.getMonth() - 1, 1);
  return formatMonth(lastMonth);
};

export const getFinancialYearStart = (referenceDate: Date, fyStartMonth?: string): string => {
  const fyStartMonthIndex = getFinancialYearStartMonthIndex(fyStartMonth ?? 'Jan');

  const fyStartDate =
    referenceDate.getMonth() >= fyStartMonthIndex
      ? new Date(referenceDate.getFullYear(), fyStartMonthIndex, 1)
      : new Date(referenceDate.getFullYear() - 1, fyStartMonthIndex, 1);

  return formatMonth(fyStartDate);
};

export const getCurrentFinancialYearStart = (fyStartMonth?: string): string =>
  getFinancialYearStart(new Date(), fyStartMonth);

export const getFinancialYearEnd = (referenceDate: Date, fyStartMonth?: string): string => {
  const fyStartMonthIndex = getFinancialYearStartMonthIndex(fyStartMonth ?? 'Jan');

  // Financial year end is the month before the start month
  const fyEndMonthIndex = (fyStartMonthIndex - 1 + 12) % 12;

  // Determine which year the FY ends in
  const fyStartDate =
    referenceDate.getMonth() >= fyStartMonthIndex
      ? new Date(referenceDate.getFullYear(), fyStartMonthIndex, 1)
      : new Date(referenceDate.getFullYear() - 1, fyStartMonthIndex, 1);

  // If end month is before start month, FY ends in the next calendar year
  const fyEndYear =
    fyEndMonthIndex < fyStartMonthIndex ? fyStartDate.getFullYear() + 1 : fyStartDate.getFullYear();

  const fyEndDate = new Date(fyEndYear, fyEndMonthIndex, 1);

  return formatMonth(fyEndDate);
};

export const getReportingPeriodDisplay = (
  dashboardReportingPeriod: any,
  fy_start_month: string | undefined,
  preset: string = 'Custom',
) => {
  let dateRange = '';

  switch (preset) {
    case 'Custom':
      if (dashboardReportingPeriod?.start && dashboardReportingPeriod?.end) {
        dateRange = ` (${dashboardReportingPeriod.start} - ${dashboardReportingPeriod.end})`;
      }
      break;
    case 'Current Month': {
      const currentMonth = getCurrentMonth();
      dateRange = ` (${currentMonth})`;
      break;
    }
    case 'Last Month': {
      const lastMonth = getLastMonth();
      dateRange = ` (${lastMonth})`;
      break;
    }
    case 'Financial Year to Last Month': {
      const fyStartMonth = fy_start_month as string | undefined;
      // Use last month as reference date to calculate the financial year
      const now = new Date();
      const lastMonthDate = new Date(now.getFullYear(), now.getMonth() - 1, 1);
      const fyStart = getFinancialYearStart(lastMonthDate, fyStartMonth);
      const lastMonth = getLastMonth();
      dateRange = ` (${fyStart} - ${lastMonth})`;
      break;
    }
    case 'This Financial Year': {
      const fyStartMonth = fy_start_month as string | undefined;
      const now = new Date();
      const fyStart = getFinancialYearStart(now, fyStartMonth);
      const fyEnd = getFinancialYearEnd(now, fyStartMonth);
      dateRange = ` (${fyStart} - ${fyEnd})`;
      break;
    }
    default:
      break;
  }

  return `${preset}${dateRange}`;
};
